import {
  ID_TOKEN_KEY,
  ACCESS_TOKEN_KEY,
  ASSO_SUBS,
  REFRESH_TOKEN_KEY,
  SUB_ID,
  IS_SSO_LOGIN_KEY,
  AUTH_SESSION_ACTIVE_KEY,
  HASURA_CLAIMS_KEY,
  SSO_CALLBACK_CODE_KEY_PREFIX
} from "constants/auth";
import {
  DATEBLOCK_VIEW_MODE_STORAGE_KEY,
  DATEBLOCK_VIEW_MODE_PROJECT_KEY
} from "entity-app/shared-components/entity-dateblock/dateblock-view-mode-toggle";
import { BASE_URL } from "services/endpoints";
import axios from "axios";
import { decodeToken, getHasuraClaims } from "./jwt-utils";
import {
  buildSubscriptionSelectRedirect,
  getPostLoginRedirectFromParams,
  getProjectInviteRedirectUrlFromParams,
  hasPendingPostLoginRedirect,
  isProjectInviteRedirectUrl,
  resolveInvitePostLoginNavigationUrl,
  resolveSubscriptionForProjectInvite,
  SSO_POST_LOGIN_REDIRECT_KEY
} from "./project-invite-redirect";
import { clearGqlExchangeCache } from "./gql-exchange-cache";
import { markSessionTerminating } from "services/session-state";
import { OPC_SYNC_STORAGE_KEY } from "services/opc-sync-state";

/**
 * Returns the subscription access token from localStorage.
 */
const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const getIDToken = () => {
  return localStorage.getItem(ID_TOKEN_KEY);
};

const getIsSSOLogin = () => {
  return localStorage.getItem(IS_SSO_LOGIN_KEY);
};

const getUser = (): any => {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    return null;
  }

  const hasuraClaims = getHasuraClaims(token);

  // replace hasura claims with decoded claims
  decoded[HASURA_CLAIMS_KEY] = hasuraClaims;

  return decoded;
};

const clearAuthSessionStorage = () => {
  try {
    sessionStorage.removeItem(SSO_POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(AUTH_SESSION_ACTIVE_KEY);
    sessionStorage.removeItem(DATEBLOCK_VIEW_MODE_STORAGE_KEY);
    sessionStorage.removeItem(DATEBLOCK_VIEW_MODE_PROJECT_KEY);
    // OpcSyncWatcher clears this itself on session teardown, but only while it
    // is mounted — and it lives on the Schedules in OPC page, so logging out
    // from anywhere else would otherwise leave a handle for the next user of
    // this tab to resume.
    sessionStorage.removeItem(OPC_SYNC_STORAGE_KEY);

    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(SSO_CALLBACK_CODE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore storage errors
  }
};

const clearAuthCredentials = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(SUB_ID);
  localStorage.removeItem(ASSO_SUBS);
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(IS_SSO_LOGIN_KEY);
  clearGqlExchangeCache();
  clearAuthSessionStorage();
};

const logout = async () => {
  // Mark teardown immediately so in-flight requests and pollers stop/cancel
  // during the logout round-trip + redirect window (prevents 401 Sentry noise).
  markSessionTerminating();
  try {
    await axios.post(`${BASE_URL}/logout`, {
      id_token: getIDToken() as string
    });
  } catch (ex) {
    console.error(ex);
  } finally {
    clearAuthCredentials();
  }
  setTimeout(() => {
    window.location.href = "/";
  }, 200);
};

const setIDToken = (token: string) => {
  localStorage.setItem(ID_TOKEN_KEY, token);
};

const setAccessToken = (token: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
};

const setRefreshToken = (token: string) => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

const setIsSSOLogin = (value: string) => {
  localStorage.setItem(IS_SSO_LOGIN_KEY, value);
};

const clearIsSSOLogin = () => {
  localStorage.removeItem(IS_SSO_LOGIN_KEY);
};

const markAuthSessionActive = () => {
  try {
    sessionStorage.setItem(AUTH_SESSION_ACTIVE_KEY, "1");
  } catch {
    // ignore storage errors
  }
};

const isAuthSessionActiveInTab = () => {
  try {
    return sessionStorage.getItem(AUTH_SESSION_ACTIVE_KEY) === "1";
  } catch {
    return false;
  }
};

const clearAuthSessionActive = () => {
  try {
    sessionStorage.removeItem(AUTH_SESSION_ACTIVE_KEY);
  } catch {
    // ignore storage errors
  }
};

const getAssociatedSubscriptions = (): Array<any> => {
  const associatedSubscriptions = localStorage.getItem(ASSO_SUBS);
  try {
    return JSON.parse(associatedSubscriptions!);
  } catch {
    return [];
  }
};

const setAssociatedSubscriptions = (associatedSubscriptions: Array<any>) => {
  try {
    localStorage.setItem(ASSO_SUBS, JSON.stringify(associatedSubscriptions));
  } catch (err) {
    console.error(err);
  }
};

const getCurrentSubscriptionId = () => {
  return localStorage.getItem(SUB_ID);
};

const setCurrentSubscriptionId = (subscriptionId: string) => {
  localStorage.setItem(SUB_ID, subscriptionId);
};

const setAccessTokenAndSubscriptionId = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") || "";
  const subscriptionId = params.get("subscriptionId") || "";

  if (!code) {
    return;
  }

  try {
    const tokenFetchUrl = `${BASE_URL}/tmp_token/${code}`;
    const tokenFetchResponse = await axios.get(tokenFetchUrl);

    const {
      accessToken,
      id_token: idToken,
      ref_token: refToken
    } = tokenFetchResponse?.data?.success || {};

    if (accessToken) {
      setAccessToken(accessToken);
    }
    if (subscriptionId) {
      setCurrentSubscriptionId(subscriptionId);
    }
    if (idToken) {
      setIDToken(idToken);
    }
    if (refToken) {
      setRefreshToken(refToken);
    }
    if (accessToken) {
      markAuthSessionActive();
    }
  } catch (error) {
    console.error("Error fetching tokens:", error);
  }
};

const getLoggedinUserId = (): any => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return "";
  }
  const hasuraClaims = getHasuraClaims(accessToken);
  const userId = hasuraClaims?.["x-hasura-user-id"];
  return userId || "";
};

const getSubscriptionId = () => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      return "";
    }
    const hasuraClaims = getHasuraClaims(accessToken);
    const subscriptionId = hasuraClaims?.["x-hasura-subscription-id"];
    return subscriptionId || "";
  } catch {
    return "";
  }
};

const getProjectId = () => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      return "";
    }
    const hasuraClaims = getHasuraClaims(accessToken);
    const projectId = hasuraClaims?.["x-hasura-project-id"];
    return projectId || "";
  } catch {
    return "";
  }
};

const isTokenExpired = (jwtToken?: string) => {
  if (!jwtToken) {
    return null;
  }

  try {
    const jwt = JSON.parse(atob(jwtToken.split(".")[1]));
    const exp = (jwt && jwt.exp && jwt.exp * 1000) || null;

    if (!exp) {
      return false;
    }
    return Date.now() > exp;
  } catch {
    return "Invalid Token";
  }
};

const isAccessTokenUsable = (accessToken: string): boolean => {
  const expired = isTokenExpired(accessToken);
  return expired !== true && expired !== "Invalid Token";
};

function getSubscriptionsForInviteResolution(): Array<{ id: string }> {
  const storedSubscriptions = getAssociatedSubscriptions();
  return Array.isArray(storedSubscriptions) ? storedSubscriptions : [];
}

function computeInviteSubscriptionNavigationUrl(
  postLoginRedirectUrl: string | null,
  projectInviteRedirectUrl: string | null
): string | null {
  const inviteSubscriptionId = resolveSubscriptionForProjectInvite(
    projectInviteRedirectUrl,
    getSubscriptionsForInviteResolution()
  );
  if (!inviteSubscriptionId) {
    return null;
  }

  return resolveInvitePostLoginNavigationUrl(
    postLoginRedirectUrl,
    projectInviteRedirectUrl
  );
}

function applyInviteSubscriptionNavigation(
  postLoginRedirectUrl: string | null,
  projectInviteRedirectUrl: string | null
): string | null {
  const inviteSubscriptionId = resolveSubscriptionForProjectInvite(
    projectInviteRedirectUrl,
    getSubscriptionsForInviteResolution()
  );
  if (!inviteSubscriptionId) {
    return null;
  }

  setCurrentSubscriptionId(inviteSubscriptionId);
  return resolveInvitePostLoginNavigationUrl(
    postLoginRedirectUrl,
    projectInviteRedirectUrl
  );
}

function resolveAuthenticatedPostLoginDestination(
  search: string,
  hash: string,
  consumePersistedSsoRedirect: boolean,
  persistInviteSubscription: boolean
): string | null {
  if (!hasPendingPostLoginRedirect(search, hash)) {
    return null;
  }

  const postLoginRedirectUrl = getPostLoginRedirectFromParams(
    search,
    hash,
    consumePersistedSsoRedirect
  );
  const projectInviteRedirectUrl =
    getProjectInviteRedirectUrlFromParams(search, hash) ||
    (isProjectInviteRedirectUrl(postLoginRedirectUrl)
      ? postLoginRedirectUrl
      : null);

  const inviteNavigation = persistInviteSubscription
    ? applyInviteSubscriptionNavigation(
        postLoginRedirectUrl,
        projectInviteRedirectUrl
      )
    : computeInviteSubscriptionNavigationUrl(
        postLoginRedirectUrl,
        projectInviteRedirectUrl
      );
  if (inviteNavigation) {
    return inviteNavigation;
  }

  if (!getCurrentSubscriptionId()) {
    return buildSubscriptionSelectRedirect(postLoginRedirectUrl);
  }

  // The "/projects" fallback is unreachable: passing the earlier
  // hasPendingPostLoginRedirect guard guarantees a non-empty redirect source,
  // so postLoginRedirectUrl is always a truthy URL at this point.
  /* v8 ignore next */
  return postLoginRedirectUrl || "/projects";
}

/** Read-only check for auth-route auto-redirect (does not consume SSO redirect). */
const peekAuthenticatedAuthRouteRedirect = (
  search: string,
  hash = ""
): string | null => {
  const accessToken = getAccessToken();
  if (!accessToken || !isAuthSessionActiveInTab()) {
    return null;
  }

  if (!isAccessTokenUsable(accessToken)) {
    clearAuthCredentials();
    return null;
  }

  return resolveAuthenticatedPostLoginDestination(search, hash, false, false);
};

/** Resolves and consumes persisted SSO redirect when applicable. */
const resolveAuthenticatedAuthRouteRedirect = (
  search: string,
  hash = ""
): string | null => {
  const accessToken = getAccessToken();
  if (!accessToken || !isAuthSessionActiveInTab()) {
    return null;
  }

  if (!isAccessTokenUsable(accessToken)) {
    clearAuthCredentials();
    return null;
  }

  return resolveAuthenticatedPostLoginDestination(search, hash, true, true);
};

const AuthUtils = {
  getAccessToken,
  getRefreshToken,
  getIDToken,
  getIsSSOLogin,
  getUser,
  logout,
  setIDToken,
  setAccessToken,
  setRefreshToken,
  setIsSSOLogin,
  clearIsSSOLogin,
  markAuthSessionActive,
  isAuthSessionActiveInTab,
  clearAuthSessionActive,
  clearAuthCredentials,
  getAssociatedSubscriptions,
  setAssociatedSubscriptions,
  getCurrentSubscriptionId,
  setCurrentSubscriptionId,
  setAccessTokenAndSubscriptionId,
  getLoggedinUserId,
  getSubscriptionId,
  getProjectId,
  isTokenExpired,
  peekAuthenticatedAuthRouteRedirect,
  resolveAuthenticatedAuthRouteRedirect
};

export default AuthUtils;
